import { supabase } from '@/integrations/supabase/client';

export type SessionStatus =
  | 'draft'
  | 'contract_pending'
  | 'contract_signed'
  | 'checklist_pending'
  | 'in_progress'
  | 'paused'
  | 'closing'
  | 'completed';

const ALLOWED_TRANSITIONS: Record<SessionStatus, SessionStatus[]> = {
  draft:             ['contract_pending'],
  contract_pending:  ['contract_signed'],
  contract_signed:   ['checklist_pending'],
  checklist_pending: ['in_progress'],
  in_progress:       ['paused', 'closing'],
  paused:            ['in_progress', 'closing'],
  closing:           ['completed'],
  completed:         [],
};

export async function transitionSession(
  sessionId: string,
  to: SessionStatus,
  extra?: Record<string, unknown>,
): Promise<void> {
  const updates: Record<string, unknown> = {
    status_v2: to,
    ...extra,
  };

  if (to === 'in_progress') {
    if (!updates.started_at) updates.started_at = new Date().toISOString();
    updates.timer_started_at = new Date().toISOString();
    updates.status           = 'in_progress';
  }
  if (to === 'paused') {
    updates.paused_at        = new Date().toISOString();
    updates.timer_started_at = null;
    updates.status           = 'paused';
  }
  if (to === 'completed') {
    updates.closed_at = new Date().toISOString();
    updates.status    = 'completed';
  }

  const { error } = await (supabase as any)
    .from('sessions')
    .update(updates)
    .eq('id', sessionId);

  if (error) throw new Error(`Transition ${to} failed: ${error.message}`);
}

export function sessionStatusLabel(status: SessionStatus, lang: 'fr' | 'en' | 'es' = 'fr'): string {
  const labels: Record<SessionStatus, Record<'fr' | 'en' | 'es', string>> = {
    draft:             { fr: 'Brouillon',          en: 'Draft',            es: 'Borrador'        },
    contract_pending:  { fr: 'Contrat en attente',  en: 'Contract pending', es: 'Contrato pend.'  },
    contract_signed:   { fr: 'Contrat signé',       en: 'Contract signed',  es: 'Contrato firm.'  },
    checklist_pending: { fr: 'Checklist',           en: 'Checklist',        es: 'Lista de checks' },
    in_progress:       { fr: 'En cours',            en: 'In progress',      es: 'En curso'        },
    paused:            { fr: 'En pause',            en: 'Paused',           es: 'En pausa'        },
    closing:           { fr: 'Clôture',             en: 'Closing',          es: 'Cierre'          },
    completed:         { fr: 'Terminée',            en: 'Completed',        es: 'Terminada'       },
  };
  return labels[status]?.[lang] ?? status;
}

export function sessionStatusColor(status: SessionStatus): string {
  const colors: Record<SessionStatus, string> = {
    draft:             'text-muted-foreground',
    contract_pending:  'text-amber-500',
    contract_signed:   'text-blue-500',
    checklist_pending: 'text-orange-500',
    in_progress:       'text-green-500',
    paused:            'text-yellow-500',
    closing:           'text-purple-500',
    completed:         'text-primary',
  };
  return colors[status] ?? 'text-muted-foreground';
}
